import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { CardButton } from '@/@bothrs-ui/web/components/primitives/card-button/CardButton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { isProduction } from '@/utils/environment'
import { useFeatureFlag } from '@/utils/feature-flag'
import { CurrentUserCard } from '../../components/current-user-card/CurrentUserCard'
import { StNavigationItem } from './ProfileNav.styled'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'

type NavItem = {
  id: string
  href: string
  icon: IconProp
  activeIcon: IconProp
  label: string
  external?: boolean
  onClick?: () => void
}

type ProfileNavProps = {
  showSwitchTenant: boolean
}

const allNavItems: NavItem[] = [
  {
    id: 'profile-information',
    href: '/profile/information',
    icon: ['fal', 'user'] as IconProp,
    activeIcon: ['fas', 'user'] as IconProp,
    label: t({
      id: 'profile.account-information.title',
      comment: 'Profile information',
      message: 'Persoonlijke gegevens',
    }),
  },
  {
    id: 'health',
    href: '/profile/health',
    icon: ['fal', 'folder-medical'] as IconProp,
    activeIcon: ['fas', 'folder-medical'] as IconProp,
    label: t({
      id: 'profile.health-profile.title',
      message: 'Gezondheidsgegevens',
    }),
  },
  {
    id: 'profile-additional-contacts',
    href: '/profile/additional-contacts',
    icon: ['fal', 'address-book'],
    activeIcon: ['fas', 'address-book'],
    label: t({
      id: 'profile.additional-contacts.title',
      comment: 'additional contacts',
      message: 'Mijn contactpersonen',
    }),
  },
  {
    id: 'profile-mandaten',
    href: '/profile/mandaten',
    icon: ['fal', 'lock'],
    activeIcon: ['fas', 'lock'],
    label: t({
      id: 'profile.account-settings.permission',
      comment: 'Permission',
      message: 'Mandaten of volmachten',
    }),
  },
  {
    id: 'profile-settings',
    href: '/profile/settings',
    icon: ['fal', 'cog'],
    activeIcon: ['fas', 'cog'],
    label: t({
      id: 'profile.account-settings.title',
      comment: 'Account settings',
      message: 'Accountinstellingen',
    }),
  },
  {
    id: 'profile-privacy',
    href: '/profile/privacy',
    icon: ['fal', 'shield-check'],
    activeIcon: ['fas', 'shield-check'],
    label: t({
      id: 'profile.account-settings.privacy',
      comment: 'Privacy',
      message: 'Privacy',
    }),
  },
]

if (!isProduction()) {
  allNavItems.push({
    id: 'profile-debug-settings',
    href: '/profile/debug-settings',
    icon: ['fal', 'cog'],
    activeIcon: ['fas', 'cog'],
    label: t({
      id: 'profile.debug-settings.title',
      message: 'Debug Settings',
    }),
  })
}

const ProfileNav = ({ showSwitchTenant }: ProfileNavProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(breakpoints.toS)
  const isDesktop = useMediaQuery(breakpoints.fromM)
  const location = useLocation()
  const navigate = useNavigate()

  const healthParametersEnabled = useFeatureFlag('health_parameters')
  const navItems = useMemo(
    () => (healthParametersEnabled ? allNavItems : allNavItems.filter((item) => item.id !== 'health')),
    [healthParametersEnabled],
  )

  // Effects
  // ------------------------------------------------------------------------- /

  useEffect(() => {
    if (location.pathname.endsWith('/profile') && isDesktop) {
      navigate('/profile/information')
    }
  }, [location, isDesktop, navigate])

  // Render Methods
  // ------------------------------------------------------------------------- /
  function renderNavigationButton(item: NavItem, index: number) {
    return isMobile ? (
      <CardButton
        key={index}
        to={item.href}
        label={item.label}
        icon={item.icon as IconProp}
        onClick={item.onClick}
        external={item.external}
        roundedTop={index === 0}
        roundedBottom={index === navItems.length - 1}
      />
    ) : (
      <StNavigationItem
        key={item.href}
        to={item.href}
        target={item.external ? '_blank' : undefined}
        onClick={item.onClick}
      >
        {({ isActive }) => (
          <>
            <FontAwesomeIcon icon={isActive ? (item.activeIcon as IconProp) : (item.icon as IconProp)} />
            <Span style={{ display: 'flex', flexGrow: 1 }} type='BodySmallRegular'>
              {item.label}
            </Span>
            {item.external && <FontAwesomeIcon icon={['fal', 'external-link']} />}
          </>
        )}
      </StNavigationItem>
    )
  }

  return (
    <ContentPageLayout
      animationkey='profile-nav'
      noScroll
      header={
        <NativePlatform>
          <AppHeader
            primaryAction={{
              label: i18n._(globalDefinitions.goBack),
              icon: ['far', 'chevron-left'],
              onClick: () => {
                navigate('/home')
              },
            }}
            title={{ title: t({ id: 'route.profile' }), as: 'h3' }}
          />
        </NativePlatform>
      }
    >
      <div style={{ paddingBottom: theme.UI.SpacingPx.Space20 }}>
        <FlexColumn gap={theme.UI.Spacing.Space6}>
          <CurrentUserCard showSwitchTenant={showSwitchTenant} />
          <FlexColumn>
            {/* Navigation items */}
            {navItems.map((item, index) => renderNavigationButton(item, index))}
          </FlexColumn>
        </FlexColumn>
      </div>
    </ContentPageLayout>
  )
}

export { ProfileNav }
